import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Form } from 'react-form';
import PropTypes from 'prop-types';
import styles from '../NextAvailable/nextAvailable.scss';
import { Textarea, CheckBox } from '../../../components/Form';
import { contactSelectedMembers } from '../../../actions/event-actions';
import { SMS_UNAVAILABLE_REASONS, buildSmsUnavailableMessage } from '../../../utils/eventSmsAvailability';

import NextAvailableSidePanel from '../NextAvailable/NextAvailableSidePanel';

const ContactForm = (props) => {
    const {
        closeSidePanel,
        selectedIndividualMemberIds,
        selectedMemberIdsFromTeams,
        event,
        smsSendingDisabled,
        smsSendingDisabledReason,
        smsSendingDisabledCutoffDisplay,
    } = props;

    const dispatch = useDispatch();

    const [sendSms, setSendSms] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);
    const [charCount, setCharCount] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (smsSendingDisabled && sendSms) {
            setSendSms(false);
        }
    }, [smsSendingDisabled, sendSms]);

    const handleInputChange = (value) => {
        setCharCount(value.length);
    };

    const onCancelClick = () => {
        closeSidePanel();
    };

    const combinedUniqueMembers = (selectedMemberIdsFromTeams, selectedIndividualMemberIds) => {
        return [...new Set([...selectedMemberIdsFromTeams, ...selectedIndividualMemberIds])];
    };

    const selectedMembers = combinedUniqueMembers(selectedMemberIdsFromTeams, selectedIndividualMemberIds);
    const playerPlurality = selectedMembers.length > 1 ? 'Players' : 'Player';
    const effectiveSendSms = smsSendingDisabled ? false : sendSms;
    const noCheckboxSelected = (!effectiveSendSms && !sendEmail);

    const handleSmsChange = (checked) => {
        if (smsSendingDisabled) {
            return;
        }
        setSendSms(checked);
    };

    const onFormSubmit = async (values) => {
        const formData = {
            memberIds: selectedMembers,
            message: values?.message,
            sendSms: effectiveSendSms,
            sendEmail,
        };

        setError(null);
        setIsSubmitting(true);

         try {
            await dispatch(contactSelectedMembers(event?.data?.id, formData));
            closeSidePanel();
        } catch (err) {
            setError('Failed to send message. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form
            onSubmit={onFormSubmit}
        >
            {(formApi) => {
                const { submitForm, values } = formApi;

                return (
                    <form onSubmit={submitForm} id="dayConfigForm" data-testid="draw-edit.contact-form.form">
                        <NextAvailableSidePanel
                            closeSideBar={closeSidePanel}
                            className={styles.contactForm}
                        >
                            <div className={styles.header} data-testid="draw-edit.contact-form.header">
                                Contact {selectedMembers.length} {playerPlurality}
                            </div>
                            <div className={styles.contentSection}>
                                <Textarea
                                    id="message"
                                    field="message"
                                    placeholder="Type your message here"
                                    rows="5"
                                    maxLength="400"
                                    onChange={handleInputChange}
                                    data-testid="draw-edit.contact-form.message-input"
                                />
                                <p className={`${styles.charCount}`} data-testid="draw-edit.contact-form.char-count">
                                    {charCount}/400 characters
                                </p>
                                <CheckBox
                                    id="email"
                                    customLabel="Email"
                                    checked={sendEmail}
                                    formGroupCssClass={`${styles.emailCheckbox}`}
                                    onChange={(checked) => setSendEmail(checked)}
                                    controlled
                                    data-testid="draw-edit.contact-form.email-checkbox"
                                />
                                {event?.data?.smsEnabled && (
                                    <>
                                        <CheckBox
                                            id="sms"
                                            customLabel="SMS"
                                            checked={effectiveSendSms}
                                            formGroupCssClass={styles.smsCheckbox}
                                            onChange={handleSmsChange}
                                            disabled={smsSendingDisabled}
                                            controlled
                                            data-testid="draw-edit.contact-form.sms-checkbox"
                                        />
                                        {smsSendingDisabled ? (
                                            <p
                                                className={`${styles.smsDetails}`}
                                                data-testid="draw-edit.contact-form.sms-disabled-message"
                                            >
                                                {buildSmsUnavailableMessage(smsSendingDisabledReason, smsSendingDisabledCutoffDisplay)}
                                            </p>
                                        ) : (
                                            <p className={`${styles.smsDetails}`} data-testid="draw-edit.contact-form.sms-details">
                                                Text messages will not be sent to players unless they have a properly formatted phone number and are opted in to receiving texts. Players can update both of these in their profile settings.
                                            </p>
                                        )}
                                    </>
                                )}
                                {error && (
                                    <p className={`${styles.errorText}`} data-testid="draw-edit.contact-form.error-text">
                                        {error}
                                    </p>
                                )}
                            </div>
                            <div className={`${styles.footer} d-flex align-items-center`}>
                                <button
                                    className={`btn btn-default ${styles.submitButton} mr8 flex-1`}
                                    type="button"
                                    onClick={onCancelClick}
                                    data-testid="draw-edit.contact-form.cancel-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    className={`btn btn-primary ${styles.submitButton} flex-1`}
                                    type="submit"
                                    disabled={!values.message || noCheckboxSelected || isSubmitting}
                                    data-testid="draw-edit.contact-form.send-btn"
                                >
                                    Send
                                </button>
                            </div>
                        </NextAvailableSidePanel>
                    </form>
                );
            }}
        </Form>
    );
};

ContactForm.propTypes = {
    closeSidePanel: PropTypes.func.isRequired,
    event: PropTypes.shape({
        data: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            smsEnabled: PropTypes.bool,
        }),
    }).isRequired,
    selectedIndividualMemberIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    selectedMemberIdsFromTeams: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    smsSendingDisabled: PropTypes.bool,
    smsSendingDisabledReason: PropTypes.oneOf([
        SMS_UNAVAILABLE_REASONS.UNPUBLISHED,
        SMS_UNAVAILABLE_REASONS.POST_EVENT_CUTOFF,
        SMS_UNAVAILABLE_REASONS.MISSING_TIMEZONE,
    ]),
    smsSendingDisabledCutoffDisplay: PropTypes.string,
};

ContactForm.defaultProps = {
    selectedIndividualMemberIds: [],
    selectedMemberIdsFromTeams: [],
    smsSendingDisabled: false,
    smsSendingDisabledReason: null,
    smsSendingDisabledCutoffDisplay: null,
};

export default ContactForm;
